import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CommonTextField from "../commonTextField/CommonTextField";
import CommonDropdown from "../commonDropDown/CommonDropDown";
import FlexibleTextarea from "../textArea/FlexibleTextarea";
import TextEditor from "../textEditor/TextEditor";
import * as yup from "yup";
import { FaLinkedin } from "react-icons/fa";
import { BsTwitterX } from "react-icons/bs";
import Earth from "../../images/svgs/Earth";
import TextInputIcon from "../Inputs/TextInputIcon";
import CertificationUpload from "../certifications/CertificationUpload";
import { getProfile } from "../../services/mentorProfile";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Sidebar from "../Sidebar";
import CustomButton from "../customButtom/CustomButton";
import { yupResolver } from "@hookform/resolvers/yup";
import { api1 } from "../../utils/axiosInstance";
import { toast } from "react-toastify";

const schema = yup.object().shape({
  isVerified: yup.boolean().required("Verification status is required"),
});

const MentorProfileView = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const mentorProfile = useSelector((state) => state.mentor.profile);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getProfile({ userId: id }));
  }, [dispatch, id]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (mentorProfile) {
      reset({
        isVerified: mentorProfile.isVerified,
        email: mentorProfile.email,
        firstName: mentorProfile.firstName,
        lastName: mentorProfile.lastName,
        gender: mentorProfile.gender,
        country: mentorProfile.country,
        state: mentorProfile.state,
        city: mentorProfile.city,
        ageGroup: mentorProfile.ageGroup,
        race: mentorProfile.race,
        bio: mentorProfile.bio,
        education: mentorProfile.higherEducationLevel,
        employmentStatus: mentorProfile.employmentStatus,
        professionalExperience: mentorProfile.professionalExperience,
        yearOfExperience: mentorProfile.yearsOfExperience,
        jobTitle: mentorProfile.currentJobOccupation,
        mentoringExperience:
          mentorProfile.formalMentoringOrCoachingExperience === "Yes" ? 0 : 1,
        yearsOfMentoring: mentorProfile.yearsOfMentoring,
        whyDoYouWantToBeMentor: mentorProfile.mentorReason,
        industry: mentorProfile.industry,
        occupations: mentorProfile.occupations,
        EmergingAICareers: mentorProfile.EmergingAICareers,
        softSkills: mentorProfile.softSkills,
        AITools: mentorProfile.AITools,
        technology: mentorProfile.technology,
        careerAspirations: mentorProfile.careerAspirations,
        linkedIn: mentorProfile.linkedIn,
        twitter:
          mentorProfile.twitter === "null" ? null : mentorProfile.twitter,
        personalWebsite:
          mentorProfile.personalWebsite === "null"
            ? null
            : mentorProfile.personalWebsite,
        CertificationName: mentorProfile.certificationName || "",
        provider: mentorProfile.certificationProvider || "",
        description: mentorProfile.certificationDescription || "",
        issueDate: mentorProfile.certificationIssueDate || "",
        expirationDate: mentorProfile.certificationExpiryDate || "",
        certificationID: mentorProfile.certificationId || "",
        certificationUrl: mentorProfile.certificationUrl || "",
        photo: mentorProfile.profileImage || "",
        cv: mentorProfile.cv || "",
      });
    }
  }, [mentorProfile, reset]);

  const handleDownload = async (fileUrl, fileName) => {
    if (!fileUrl) {
      alert("File not available.");
      return;
    }

    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Revoke the object URL to free up memory
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file.");
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // Update the backend
      await api1.put(`/api/mentorProfile/verify/${id}`);
      // Update local state for UI sync
      dispatch(getProfile({ userId: id }));
      // Reset the form with new values
      reset({ isVerified: data.isVerified });

      toast.success("Verification Updated");
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to update verification.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sidebar>
      <div className="text-xl text-custom-pink font-bold my-4">
        Mentorship Application Form
      </div>
      <div className="w-full mx-auto bg-white shadow-lg rounded-lg">
        <div className="flex  justify-between p-6 w-full">
          <div>
            <h2 className="text-2xl font-semibold mb-4">Profile Info</h2>
            <p>Please fill baseline information about yourself</p>
          </div>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="w-2/4 flex gap-4 items-end"
          >
            <div className="w-full">
              <CommonDropdown
                label="Verification Status"
                name="isVerified"
                required
                register={register}
                errors={errors}
                defaultValue={mentorProfile?.isVerified}
                options={[
                  { value: true, label: "Verified" },
                  { value: false, label: "Not Verified" },
                ]}
              />
            </div>
            <div>
              <CustomButton
                type="submit"
                label={loading ? "Updating..." : "Update"}
                buttonClass="bg-custom-gradient px-[20px] py-[9px] rounded-lg w-full sm:w-[138px]"
                disabled={loading}
              />
            </div>
          </form>
        </div>
        <div className="border-b"></div>
        <div className="space-y-4 p-6">
          <div className="font-bold text-xl">Personal Information</div>
          {/* Personal Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4">
            <CommonTextField
              name="firstName"
              readOnly={true}
              label="First Name"
              register={register}
              required
              errors={errors}
              placeholder="Enter First Name"
              defaultValue={mentorProfile?.firstName}
            />
            <CommonTextField
              name="lastName"
              label="Last Name"
              register={register}
              required
              errors={errors}
              placeholder="Enter Last Name"
              readOnly={true}
              defaultValue={mentorProfile?.lastName}
            />
            <CommonTextField
              label="Gender"
              name="gender"
              required
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.gender}
              readOnly={true}
            />
            <CommonTextField
              label="Country"
              name="country"
              required
              readOnly={true}
              register={register}
              search={true}
              errors={errors}
              defaultValue={mentorProfile?.country}
            />

            <CommonTextField
              name="state"
              readOnly={true}
              label="State"
              register={register}
              required
              errors={errors}
              placeholder="State / Province"
              defaultValue={mentorProfile?.state}
            />
            <CommonTextField
              name="city"
              label="City"
              readOnly={true}
              register={register}
              required
              errors={errors}
              placeholder="City"
              defaultValue={mentorProfile?.city}
            />
            <CommonTextField
              label="Age Group"
              name="ageGroup"
              register={register}
              readOnly={true}
              errors={errors}
              defaultValue={mentorProfile?.ageGroup}
            />
            <CommonTextField
              label="Race"
              name="race"
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.race}
            />
            <CommonTextField
              label="Email"
              name="email"
              readOnly={true}
              required
              register={register}
              errors={errors}
              placeholder="Email"
              defaultValue={mentorProfile?.email}
            />
            <FlexibleTextarea
              name="bio"
              placeholder="Bio"
              label="Bio"
              readOnly={true}
              outerClassName="w-full col-span-1 md:col-span-2"
              errors={errors}
              register={register}
              value={mentorProfile?.bio}
            />
          </div>

          <div className="font-bold text-xl">
            Educational & Professional Background
          </div>
          {/* Educational & Professional Background */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4">
            <CommonTextField
              label="Highest Education level"
              name="education"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.higherEducationLevel}
            />
            <CommonTextField
              label="Employment Status"
              name="employmentStatus"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.employmentStatus}
            />
            <CommonTextField
              label="Professional Experience"
              name="professionalExperience"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.professionalExperience}
            />
            <CommonTextField
              type="number"
              name="yearsOfExperience"
              label="Years of Work Experience"
              register={register}
              readOnly={true}
              required
              errors={errors}
              defaultValue={mentorProfile?.yearOfExperience}
            />

            <CommonTextField
              label="Current Job / Experience"
              name="jobTitle"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.currentJobOccupation}
            />
          </div>

          <div className="font-bold text-xl">Mentoring Experiences</div>
          {/* Mentoring Experience */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CommonTextField
              label="Any Formal Mentoring or Coaching Experience"
              name="mentoringExperience"
              required
              register={register}
              readOnly={true}
              errors={errors}
              defaultValue={
                mentorProfile?.formalMentoringOrCoachingExperience === 0
                  ? "Yes"
                  : "No"
              }
            />
            <CommonTextField
              label="Years of Mentoring Experience"
              name="yearsOfMentoring"
              readOnly={true}
              required
              register={register}
              errors={errors}
              defaultValue={`${mentorProfile?.yearsOfMentoring}`}
            />
          </div>

          <FlexibleTextarea
            name={"mentorReason"}
            readOnly={true}
            placeholder="Why do you want to be a mentor?"
            label={"Why Do You Want To Be a Mentor "}
            outerClassName="w-full col-span-2"
            errors={errors} // ✅ Fix: Ensure this is passed
            register={register}
            value={mentorProfile?.whyDoYouWantToBeMentor}
          />

          <div className="font-bold text-xl border-t pt-4">
            Mentorship Focus Areas
          </div>

          {/* Skills */}
          <div className="grid grid-cols-2 gap-4">
            <CommonTextField
              label="Industry"
              name="industry"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.industry}
            />
            <CommonTextField
              label="Occupations"
              name="occupations"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.occupations}
            />
            <CommonTextField
              label="Emerging AI Careers"
              name="EmergingAICareers"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.EmergingAICareers}
            />
            <CommonTextField
              label="Soft Skills"
              name="softSkills"
              required
              readOnly={true}
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.softSkills}
            />
            <CommonTextField
              label="Technology"
              name="technology"
              required
              register={register}
              readOnly={true}
              errors={errors}
              defaultValue={mentorProfile?.technology}
            />
            <CommonTextField
              label="AI Tools"
              name="AITools"
              readOnly={true}
              required
              register={register}
              errors={errors}
              defaultValue={mentorProfile?.AITools}
            />
          </div>
          {/* Career Aspirations */}
          <TextEditor
            label="Career Aspirations"
            name="careerAspirations"
            readOnly={true}
            errors={errors}
            defaultValue={mentorProfile?.careerAspirations}
            required
            control={control}
          />

          <div className="font-bold text-xl border-t pt-4">
            Certification Upload{" "}
            <span className="font-normal text-sm text-gray-400">
              {" "}
              (optional)
            </span>
          </div>
          <CertificationUpload
            register={register}
            handleSubmit={handleSubmit}
            errors={errors}
            readOnly={true}
            setValue={setValue}
          />

          {/* Social Media Links */}
          <div className="font-bold text-xl border-t pt-4">
            Social Media Links
          </div>
          <div className="flex justify-between flex-wrap">
            <TextInputIcon
              type="url"
              name="linkedIn"
              label="LinkedIn URL"
              register={register}
              required
              errors={errors}
              defaultValue={mentorProfile?.linkedIn}
              placeholder="LinkedIn URL"
              icon={<FaLinkedin className="text-[#0270AD] text-2xl " />}
              iconPosition="left"
            />
            <TextInputIcon
              type="url"
              name="twitter"
              label="Twitter URL (optional)"
              register={register}
              errors={errors}
              placeholder="Twitter URL"
              icon={
                <BsTwitterX className="text-white p-1 text-2xl bg-black " />
              }
              iconPosition="left"
            />

            <TextInputIcon
              type="url"
              name="personalWebsite"
              label="Personal Website (optional)"
              register={register}
              errors={errors}
              placeholder="Personal Website"
              icon={<Earth className="w-5" />}
              iconPosition="left"
            />
          </div>

          <div className="font-bold text-xl border-t pt-4">
            Documents Uploads
          </div>

          <div className="w-full flex flex-col md:flex-row gap-4">
            <CustomButton
              type="button"
              label={"Download Photo"}
              buttonClass="bg-custom-gradient px-[20px] py-[9px] rounded-lg w-full sm:w-[138px]"
              onClick={() =>
                handleDownload(mentorProfile?.profileImage, "photo")
              }
            />
            <CustomButton
              type="button"
              label={"Download CV"}
              buttonClass="bg-custom-gradient px-[20px] py-[9px] rounded-lg w-full sm:w-[138px]"
              onClick={() => handleDownload(mentorProfile?.cv, "cv")}
            />
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default MentorProfileView;
